/**
 * Append-only audit / event primitive — the backbone of trust, replay and
 * post-incident review. Every context change, insight shown/resolved, and user
 * action is recorded here. Records are immutable once appended.
 *
 * Pure: the in-memory log is a value; persistence (Table Storage) lives in the
 * api `data/` layer and adapts to this shape.
 */

import type { FactSource } from './fact.js';

export type AuditEventType =
  | 'incident.created'
  | 'incident.stage-changed'
  | 'incident.type-changed'
  | 'context.updated'
  | 'itasc.captured'
  | 'insight.shown'
  | 'insight.acknowledged'
  | 'insight.actioned'
  | 'insight.dismissed'
  | 'insight.auto-resolved'
  | 'sitrep.generated'
  | 'export.generated'
  | 'standdown.checked'
  | 'review.feedback'
  | 'localnote.created'
  | 'review.completed';

export interface AuditEvent {
  readonly id: string;
  readonly incidentId: string;
  /** ISO timestamp (incident clock time). */
  readonly at: string;
  readonly type: AuditEventType;
  /** Who/what caused it (operator id, 'system', 'duty-assistant'). */
  readonly actor: string;
  readonly source: FactSource;
  /** Structured detail; shape depends on `type`. */
  readonly payload?: Readonly<Record<string, unknown>>;
}

export interface NewAuditEvent {
  incidentId: string;
  type: AuditEventType;
  actor: string;
  source: FactSource;
  at?: string;
  payload?: Record<string, unknown>;
}

let counter = 0;
/** Monotonic-ish id; persistence layer may substitute a ULID/rowKey. */
function nextId(): string {
  counter += 1;
  return `evt_${Date.now().toString(36)}_${counter.toString(36)}`;
}

export function createAuditEvent(e: NewAuditEvent): AuditEvent {
  return {
    id: nextId(),
    incidentId: e.incidentId,
    at: e.at ?? new Date().toISOString(),
    type: e.type,
    actor: e.actor,
    source: e.source,
    payload: e.payload ? Object.freeze({ ...e.payload }) : undefined,
  };
}

/** Immutable append-only log. `append` returns a new log; the input is unchanged. */
export interface AuditLog {
  readonly events: readonly AuditEvent[];
}

export const emptyAuditLog = (): AuditLog => ({ events: [] });

export function append(log: AuditLog, event: NewAuditEvent | AuditEvent): AuditLog {
  const built = 'id' in event ? event : createAuditEvent(event);
  return { events: [...log.events, built] };
}

/** Events for one incident, oldest first. */
export function eventsFor(log: AuditLog, incidentId: string): AuditEvent[] {
  return log.events.filter((e) => e.incidentId === incidentId);
}
